/**
 * Open Chairs Service — PUBLIC (walk-in) edition
 * ------------------------------------------------------------------
 * Ported from the customer-portal dashboard "Open Chairs" tile (Jose 2026-09-18),
 * adapted for the no-login WALK-IN page (Jose 2026-10-19).
 *
 * It answers one question nothing else on the public site did:
 *   "What are the SOONEST open slots across ALL barbers, right now?"
 * It fans out over the SAME per-barber availability the guest Book Now page uses
 * (bookingService.getAvailableSlots = shop-hours ∩ barber-schedule ∩ free, per-shop
 * rules applied) and merges by time — so an Open-Chairs slot is bookable for exactly
 * the barbers listed on it. The real write still goes through bookingService
 * .createAppointment, whose server-side identity match + write-time double-book guard
 * are the final referees.
 *
 * WALK-IN options (Jose 2026-10-27): round candidates to a configurable grid (intervalMin → :00/:05/:10),
 * never offer sooner than now + bufferMinutes, only within the next lookAheadMinutes, and use minFitMinutes
 * (15) as the DISCOVERY fit so a tight gap a quick cut can use still surfaces. The result is the SOONEST
 * open chair ("next open chair at 9:05 · in 2 min"), not the next quarter hour.
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { bookingService } from './bookingService';
import { isBookableBarber } from '../shared/barberStatus';
import { parseTimeToMinutes } from '../shared/availability';
import { ymd } from '../shared/dateUtils';

/** One soonest-time slot, carrying every barber free at that exact time (for auto-match). */
export interface OpenChairSlot {
  time: string;                       // 12-hour label, e.g. "2:15 PM" — passed straight to createAppointment
  minutes: number;                    // minutes-from-midnight, for sorting
  barbers: { id: string; name: string }[];
}

export interface OpenChairs {
  date: string;                       // YYYY-MM-DD the slots belong to
  dayLabel: string;                   // "today" | "tomorrow" | weekday name
  slots: OpenChairSlot[];             // soonest-first, capped
}

class OpenChairsService {
  /**
   * Soonest open slots across ALL active barbers.
   * Walks today first; if today is fully booked / closed, rolls forward to the
   * next open day (up to a week) so the list is never empty when the shop has capacity.
   */
  async getOpenChairs(opts?: {
    maxSlots?: number;
    searchDays?: number;
    intervalMin?: number;        // WALK-IN: candidate grid step (e.g. 5 → :00/:05/:10 marks)
    lookAheadMinutes?: number;   // WALK-IN: only show slots within the next N minutes today
    bufferMinutes?: number;      // WALK-IN: prep head-start — never offer a slot sooner than now + this
    minFitMinutes?: number;      // WALK-IN: shortest service that may fit (15 → quick-cut squeeze allowed)
    fitDurationMin?: number;     // alias of minFitMinutes for older callers
  }): Promise<OpenChairs> {
    const maxSlots = opts?.maxSlots ?? 5;
    const searchDays = opts?.searchDays ?? 7;
    const intervalMin = opts?.intervalMin;             // undefined → shop's normal interval
    const lookAheadMinutes = opts?.lookAheadMinutes;
    const bufferMinutes = Math.max(0, opts?.bufferMinutes ?? 0);
    // WALK-IN "next open chair" (Jose 2026-10-27): surface the SOONEST opening, rounded to the grid.
    // DISCOVERY fit = minFitMinutes (default 15) so a tight gap a quick cut can use still shows. The
    // per-SERVICE fit (fades need the full 30) is enforced later by re-checking the chosen service's
    // real duration against the barber's availability — a 30-min fade simply isn't offered a 15-min gap.
    const minFitMinutes = opts?.minFitMinutes ?? opts?.fitDurationMin ?? 15;

    // Load barbers the SAME way the real booking flow does: read the collection directly + the shared
    // isBookableBarber visibility brain. NOT bookingService.getBarbers() — that filters isActive==true,
    // a field no barber has set, so it returns zero. (Jose 2026-09-18 root-cause)
    const bSnap = await getDocs(collection(db, 'barbers'));
    const barbers = bSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() as any) }))
      .filter((b) => isBookableBarber(b) && b.name && String(b.name).trim()); // named + bookable ("any barber")
    const today = new Date();

    for (let dayOffset = 0; dayOffset < searchDays; dayOffset++) {
      const d = new Date(today);
      d.setDate(today.getDate() + dayOffset);
      const dateStr = ymd(d);
      // On today, hide slots before "now + prep buffer" (the soonest a walk-in could realistically start).
      const nowMin = dayOffset === 0 ? today.getHours() * 60 + today.getMinutes() : -1;
      const minStart = nowMin + Math.max(1, bufferMinutes); // buffer 0 → next whole minute onward

      // Fan out over every barber's real availability for this date (walk-in grid override passed through).
      const perBarber = await Promise.all(
        barbers.map(async (b) => ({
          barber: b,
          slots: await bookingService.getAvailableSlots(b.id, dateStr, intervalMin, minFitMinutes).catch(() => [] as string[]),
        }))
      );

      // Merge by time → which barbers are free at each label.
      const timeMap: Record<string, OpenChairSlot> = {};
      for (const { barber, slots } of perBarber) {
        for (const label of slots) {
          const minutes = parseTimeToMinutes(label);
          if (minutes == null) continue;
          if (dayOffset === 0 && minutes < minStart) continue; // past / inside prep buffer today
          if (intervalMin && minutes % intervalMin !== 0) continue; // clean clock marks (:00/:05/:10…)
          if (dayOffset === 0 && lookAheadMinutes && minutes > nowMin + lookAheadMinutes) continue; // WALK-IN window
          const entry = timeMap[label] || { time: label, minutes, barbers: [] };
          entry.barbers.push({ id: barber.id, name: barber.name });
          timeMap[label] = entry;
        }
      }

      const merged = Object.values(timeMap);
      if (merged.length > 0) {
        const slots = merged
          .sort((a, b) => a.minutes - b.minutes)
          .slice(0, maxSlots);
        const dayLabel =
          dayOffset === 0 ? 'today' :
          dayOffset === 1 ? 'tomorrow' :
          d.toLocaleDateString('en-US', { weekday: 'long' });
        return { date: dateStr, dayLabel, slots };
      }
    }

    return { date: ymd(today), dayLabel: 'today', slots: [] };
  }
}

export const openChairsService = new OpenChairsService();
